/**
 * The rule editor: a route, a modal, and the most heavily validated surface in the application —
 * validated entirely by the server.
 *
 * Every keystroke asks the dry-run endpoint, which runs the identical check the write path runs.
 * That is what stops the editor disagreeing with the save, which is the bug this design prevents
 * rather than merely a duplication of effort.
 *
 * Cancel and Escape navigate to the parent route, so the browser back button closes the modal.
 */

import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Modal, ModalSpacer, ConfirmDialog } from "~/ui/Modal";
import { Button } from "~/ui/Button";
import {
  Field,
  TextInput,
  DateInput,
  NumberInput,
  UrlInput,
  Select,
  Textarea,
  PeriodInput,
} from "~/ui/Field";
import { StatusScopeField } from "~/features/rules/StatusScopeField";
import { ValidationMessages, Callout } from "~/ui/Feedback";
import { Pill } from "~/ui/Pill";
import { useDraft } from "~/hooks/useDraft";
import { useDebouncedValidation } from "~/hooks/useDebouncedValidation";
import { validateRuleDraft } from "~/api/validate";
import { useSaveRule, useDeleteRule } from "~/api/mutations";
import { useToast } from "~/hooks/useToast";
import { ApiError, problemsOf } from "~/api/client";
import { routes } from "~/lib/routes";
import type * as T from "~/api/schema";
import { fmtDate, plural } from "~/lib/format";
import { useMatterView } from "../matter/useMatterView";
import { vocabularyOf } from "../matter/describe";
import {
  blankDraft,
  draftFromRule,
  nextVersion,
  newCitation,
  parametersFor,
  toRequest,
  type DraftCitation,
  type DraftVersion,
  type RuleDraft,
} from "./draft";
import styles from "./RuleEditor.module.css";

const CITATION_ROLES = [
  { value: "primary", label: "Primary" },
  { value: "consolidating", label: "Consolidating" },
  { value: "amending", label: "Amending" },
  { value: "commentary", label: "Commentary" },
  { value: "practice_note", label: "Practice note" },
] as const;

export function RuleEditorRoute() {
  const { lane: laneParam = "" } = useParams();
  const view = useMatterView();
  const navigate = useNavigate();

  const { panel, meta, code, matter, dates } = view;
  const close = () => void navigate(routes.rules(code, matter, dates));

  if (!panel.data || !meta.data) return null;

  const creating = laneParam === "new";
  const held = panel.data.rules.find((rule) => rule.lane === laneParam);
  const catalogue = meta.data.ruleCatalogue.find(
    (entry) => entry.matter === matter && entry.lane === laneParam,
  );

  return (
    <RuleEditor
      key={laneParam}
      code={code}
      matter={matter}
      creating={creating}
      initial={
        held
          ? draftFromRule(held)
          : blankDraft(creating ? "" : laneParam, catalogue, panel.data.filing)
      }
      etag={held?.etag ?? null}
      onClose={close}
    />
  );
}

interface RuleEditorProps {
  code: string;
  matter: string;
  creating: boolean;
  initial: RuleDraft;
  etag: string | null;
  onClose: () => void;
}

function RuleEditor({
  code,
  matter,
  creating,
  initial,
  etag,
  onClose,
}: RuleEditorProps) {
  const { meta, panel } = useMatterView();
  const vocab = vocabularyOf(meta.data);
  const save = useSaveRule(code, matter);
  const remove = useDeleteRule(code, matter);
  const toast = useToast();

  const { draft, dirty, update } = useDraft<RuleDraft>(initial);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [conflict, setConflict] = useState<Record<string, unknown> | null>(
    null,
  );

  const today = panel.data?.filing ?? meta.data?.today ?? "";
  const body = useMemo(() => toRequest(draft), [draft]);

  const check = useDebouncedValidation({
    body,
    enabled: draft.lane.trim().length > 0,
    validate: (payload, signal) =>
      validateRuleDraft(code, matter, draft.lane, payload, signal),
  });

  const triggerOptions = (meta.data?.triggers ?? []).map((term) => ({
    value: term.code,
    label: term.label,
  }));
  const shapeOptions = (meta.data?.ruleShapes ?? []).map((term) => ({
    value: term.code,
    label: term.label,
  }));
  const unitOptions = (meta.data?.periodUnits ?? []).map((term) => ({
    value: term.code,
    label: term.label,
  }));

  /**
   * Manage's sub-matter vocabulary for this matter. Null means every sub-matter — an absence and
   * not an unknown — so the empty option is a real answer rather than a prompt to choose.
   */
  const subMatterOptions = [
    { value: "", label: "Every sub-matter" },
    ...(meta.data?.manageTaxonomy?.subMatterSubTypes ?? [])
      .filter((term) => term.matter === matter)
      .map((term) => ({ value: term.title, label: term.title })),
  ];

  const statusOptions = (meta.data?.manageTaxonomy?.statuses ?? []).filter(
    (term) => term.matter === matter,
  );

  /** The catalogue seeds the field; it does not bound it. */
  const catalogueNames = (meta.data?.ruleCatalogue ?? [])
    .filter((entry) => entry.matter === matter)
    .map((entry) => entry.lane);

  const updateVersion = (key: string, patch: Partial<DraftVersion>) =>
    update((current) => ({
      ...current,
      versions: current.versions.map((version) =>
        version.key === key ? { ...version, ...patch } : version,
      ),
    }));

  const attemptClose = () => {
    if (dirty) {
      setConfirmingLeave(true);
      return;
    }
    onClose();
  };

  const blocked = check.errors.length > 0 || draft.lane.trim().length === 0;

  return (
    <>
      <Modal
        title={creating ? "Add a rule" : draft.lane || "Edit a rule"}
        subtitle="A rule is a chain of dated statutory versions, and exactly one of them governs any given filing date. That is the invariant this editor exists to protect."
        onDismiss={attemptClose}
        footer={
          <>
            <Button onClick={attemptClose}>Cancel</Button>
            {etag ? (
              <Button
                variant="danger"
                onClick={() => setConfirmingDelete(true)}
              >
                Delete this rule
              </Button>
            ) : null}
            <ModalSpacer />
            <Button
              variant="primary"
              inactive={blocked}
              pending={save.isPending}
              onClick={() =>
                save.mutate(
                  { lane: draft.lane, body, ...(etag ? { etag } : {}) },
                  {
                    onSuccess: ({ written, held }) => {
                      if (held) {
                        toast.show(
                          "ok",
                          `${draft.lane} is waiting for a second person to approve it. Nothing has changed yet.`,
                        );
                        onClose();
                        return;
                      }
                      for (const warning of (written as T.RuleWrite).warnings ??
                        [])
                        toast.show("ok", warning);
                      toast.show("ok", `${draft.lane} saved.`);
                      onClose();
                    },
                    onError: (error) => {
                      if (error instanceof ApiError && error.isConflict) {
                        setConflict(error.current);
                        return;
                      }
                      for (const problem of problemsOf(error))
                        toast.show("error", problem);
                    },
                  },
                )
              }
            >
              Save this rule
            </Button>
          </>
        }
      >
        {conflict ? (
          <ConflictView
            current={conflict}
            onDismiss={() => setConflict(null)}
          />
        ) : null}

        <div className={styles.grid}>
          <Field
            label="Rule"
            hint="The known list seeds this field; it does not bound it. A rule the engine has never heard of is still a rule this register has."
            grow={2}
          >
            <TextInput
              list="rule-catalogue"
              value={draft.lane}
              onChange={(event) => update({ lane: event.currentTarget.value })}
            />
          </Field>
          <datalist id="rule-catalogue">
            {catalogueNames.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          <Field
            label="Shape"
            hint="Changing the shape resets every version's parameters, because a term and a deadline do not take the same ones."
          >
            <Select
              value={draft.shape}
              options={shapeOptions}
              onChange={(shape) =>
                update((current) => ({
                  ...current,
                  shape,
                  versions: current.versions.map((version) => ({
                    ...version,
                    ...parametersFor(shape),
                  })),
                }))
              }
            />
          </Field>
        </div>

        <div className={styles.grid} style={{ marginTop: 12 }}>
          <Field
            label="Adopted here"
            hint="When this register took the rule on. Leave empty if it always had it."
          >
            <DateInput
              value={draft.adopted ?? ""}
              onChange={(next) => update({ adopted: next || null })}
            />
          </Field>
          <Field
            label="Repealed here"
            hint="When it ceased to hold the rule at all."
          >
            <DateInput
              value={draft.repealed ?? ""}
              onChange={(next) => update({ repealed: next || null })}
            />
          </Field>
          <Field
            label="Sub-matter"
            hint="Which sub-matter this rule is about. 'Every sub-matter' is an answer, not a gap — and a rule naming one beats it where both exist."
          >
            <Select
              value={draft.subMatter ?? ""}
              options={subMatterOptions}
              onChange={(next) => update({ subMatter: next || null })}
            />
          </Field>
        </div>
        <p className={styles.note}>
          A rule repealed and later reintroduced is two adoption periods on one
          rule. Record the first here and add the second once the chain reflects
          it — the engine holds a rule as a window with a start and possibly an
          end, not as a permanent property.
        </p>

        <div className={styles.versionList}>
          {draft.versions.map((version, index) => (
            <div className={styles.version} key={version.key}>
              <div className={styles.versionHead}>
                <strong>Version {index + 1}</strong>
                {version.id ? (
                  <Pill tone="neutral" size="mini">
                    Held
                  </Pill>
                ) : (
                  <Pill tone="sugg" size="mini">
                    New
                  </Pill>
                )}
                <span className={styles.spacer} />
                {draft.versions.length > 1 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      update((current) => ({
                        ...current,
                        versions: current.versions.filter(
                          (entry) => entry.key !== version.key,
                        ),
                      }))
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </div>

              <div className={styles.grid}>
                <Field label="In force from">
                  <DateInput
                    value={version.from}
                    onChange={(next) =>
                      updateVersion(version.key, { from: next })
                    }
                  />
                </Field>
                <Field
                  label="Until"
                  hint={
                    version.inherited && version.to
                      ? `Inherited from the next version's commencement, not recorded. Typing here records it.`
                      : "Empty means still in force."
                  }
                >
                  <DateInput
                    value={version.to ?? ""}
                    onChange={(next) =>
                      updateVersion(version.key, {
                        to: next || null,
                        inherited: false,
                      })
                    }
                  />
                </Field>
                <Field label="Counted from">
                  <Select
                    value={version.trigger}
                    options={triggerOptions}
                    onChange={(trigger) =>
                      updateVersion(version.key, {
                        trigger: trigger as DraftVersion["trigger"],
                      })
                    }
                  />
                </Field>
                <StatusScopeField
                  scope={version.statusScope}
                  statuses={statusOptions}
                  onChange={(statusScope) =>
                    updateVersion(version.key, { statusScope })
                  }
                />
              </div>

              <div className={styles.grid} style={{ marginTop: 10 }}>
                {draft.shape === "deadline" ? (
                  <Field label={vocab.fieldLabel("period")}>
                    <PeriodInput
                      value={version.period ?? { value: 1, unit: "month" }}
                      units={unitOptions}
                      onChange={(period) =>
                        updateVersion(version.key, { period })
                      }
                    />
                  </Field>
                ) : null}

                {draft.shape === "term" ? (
                  <>
                    <Field label={vocab.fieldLabel("first_term")}>
                      <PeriodInput
                        value={version.firstTerm ?? { value: 10, unit: "year" }}
                        units={unitOptions}
                        onChange={(firstTerm) =>
                          updateVersion(version.key, { firstTerm })
                        }
                      />
                    </Field>
                    <Field label={vocab.fieldLabel("later_term")}>
                      <PeriodInput
                        value={version.laterTerm ?? { value: 10, unit: "year" }}
                        units={unitOptions}
                        onChange={(laterTerm) =>
                          updateVersion(version.key, { laterTerm })
                        }
                      />
                    </Field>
                  </>
                ) : null}

                {draft.shape === "annuity" ? (
                  <>
                    <Field label={vocab.fieldLabel("max_term")}>
                      <PeriodInput
                        value={version.maxTerm ?? { value: 20, unit: "year" }}
                        units={unitOptions}
                        onChange={(maxTerm) =>
                          updateVersion(version.key, { maxTerm })
                        }
                      />
                    </Field>
                    <Field label={vocab.fieldLabel("first_annuity_year")}>
                      <NumberInput
                        min={1}
                        value={version.firstAnnuityYear ?? 1}
                        onChange={(firstAnnuityYear) =>
                          updateVersion(version.key, { firstAnnuityYear })
                        }
                      />
                    </Field>
                  </>
                ) : null}
              </div>

              <div className={styles.citations}>
                <p className={styles.note}>
                  A researched version needs at least one primary citation.
                  Whether a source counts as official is computed by the server
                  against this register&rsquo;s own list — never claimed here.
                </p>
                {version.citations.map((citation) => (
                  <div className={styles.citation} key={citation.key}>
                    <div className={styles.citationHead}>
                      <Field label="Address" grow={2}>
                        <UrlInput
                          value={citation.url ?? ""}
                          onChange={(url) =>
                            updateVersion(version.key, {
                              citations: version.citations.map((entry) =>
                                entry.key === citation.key
                                  ? { ...entry, url }
                                  : entry,
                              ),
                            })
                          }
                        />
                      </Field>
                      <Field label="Role">
                        <Select
                          value={citation.role ?? "primary"}
                          options={CITATION_ROLES.map((role) => ({
                            value: role.value as DraftCitation["role"],
                            label: role.label,
                          }))}
                          onChange={(role) =>
                            updateVersion(version.key, {
                              citations: version.citations.map((entry) =>
                                entry.key === citation.key
                                  ? {
                                      ...entry,
                                      role: role as typeof entry.role,
                                    }
                                  : entry,
                              ),
                            })
                          }
                        />
                      </Field>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          updateVersion(version.key, {
                            citations: version.citations.filter(
                              (entry) => entry.key !== citation.key,
                            ),
                          })
                        }
                      >
                        Remove
                      </Button>
                    </div>
                    <Field label="The words of the provision, verbatim">
                      <Textarea
                        rows={2}
                        value={citation.quote ?? ""}
                        onChange={(event) =>
                          updateVersion(version.key, {
                            citations: version.citations.map((entry) =>
                              entry.key === citation.key
                                ? { ...entry, quote: event.currentTarget.value }
                                : entry,
                            ),
                          })
                        }
                      />
                    </Field>
                  </div>
                ))}
                <Button
                  size="sm"
                  onClick={() =>
                    updateVersion(version.key, {
                      citations: [...version.citations, newCitation()],
                    })
                  }
                >
                  Add a citation
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={() =>
            update((current) => ({
              ...current,
              versions: [...current.versions, nextVersion(current, today)],
            }))
          }
        >
          Add a version
        </Button>
        <p className={styles.note}>
          A new version starts where the last one ended — the only start that
          cannot overlap it. Moving it earlier is allowed, and the engine will
          tell you if two versions end up in force on the same day.
        </p>

        <ValidationMessages
          tone="error"
          messages={check.errors}
          pending={check.pending}
        />
        <ValidationMessages tone="warn" messages={check.warnings} />
        {check.unavailable ? (
          <Callout tone="warn" title="The draft could not be checked just now">
            {check.unavailable} The save will still be checked by the engine
            before anything is written.
          </Callout>
        ) : null}
      </Modal>

      {confirmingLeave ? (
        <ConfirmDialog
          title="Discard these changes?"
          body={
            <p>
              This draft has edits that have not been saved. Leaving the editor
              discards them; the rule keeps the chain it already has.
            </p>
          }
          confirmLabel="Discard them"
          cancelLabel="Keep editing"
          destructive
          onCancel={() => setConfirmingLeave(false)}
          onConfirm={onClose}
        />
      ) : null}

      {confirmingDelete && etag ? (
        <ConfirmDialog
          title={`Delete ${draft.lane}?`}
          body={
            <p>
              The rule and its {plural(draft.versions.length, "version")} will
              stop producing dates for every filing at this register. The
              history is kept — a deletion is a recorded event, not an erasure —
              but nothing will be calculated from it.
            </p>
          }
          confirmLabel="Delete the rule"
          cancelLabel="Keep it"
          destructive
          pending={remove.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() =>
            remove.mutate(
              { lane: draft.lane, etag },
              {
                onSuccess: ({ held }) => {
                  toast.show(
                    "ok",
                    held
                      ? `Removing ${draft.lane} is waiting for a second person. It is still there.`
                      : `${draft.lane} was deleted.`,
                  );
                  onClose();
                },
                onError: (error) => {
                  for (const problem of problemsOf(error))
                    toast.show("error", problem);
                },
              },
            )
          }
        />
      ) : null}
    </>
  );
}

/**
 * A 409 shows what changed underneath rather than discarding the user's work. The server sends
 * the current representation in the error, so the choice is informed.
 */
function ConflictView({
  current,
  onDismiss,
}: {
  current: Record<string, unknown> | null;
  onDismiss: () => void;
}) {
  const rule = current as {
    lane?: string;
    versions?: Array<{ from: string; to: string | null }>;
  } | null;

  return (
    <div className={styles.conflict} role="alert">
      <strong>This rule changed while you were editing it.</strong>
      <p>
        Nothing was written. Your draft is still on screen below; what the
        engine now holds is on the right. Re-read it before saving again, or
        close and reopen the editor to start from the current chain.
      </p>
      <div className={styles.conflictColumns}>
        <div className={styles.conflictColumn}>
          <h4>What the engine now holds</h4>
          {rule?.versions?.length ? (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {rule.versions.map((version) => (
                <li key={`${version.from}-${version.to}`}>
                  {fmtDate(version.from)} —{" "}
                  {version.to ? fmtDate(version.to) : "in force"}
                </li>
              ))}
            </ul>
          ) : (
            <p>The engine did not send a chain with the conflict.</p>
          )}
        </div>
        <div className={styles.conflictColumn}>
          <h4>What you have</h4>
          <p>Your draft, unchanged, in the form below.</p>
        </div>
      </div>
      <p style={{ marginTop: 10 }}>
        <Button size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </p>
    </div>
  );
}
